import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureLike } from 'ol/Feature';

// Material-UI Core Components
import { CircularProgress, Box, Pagination, Stack, Icon, Paper, Typography, Chip } from '@mui/material';

// Material-UI Icons
import { Edit, Vrpano } from '@mui/icons-material';

// Custom Components
import CommentIcon from '@mui/icons-material/SpeakerNotesOutlined';
import MapCard from '@/ui/MapCard/MapCard';
import MapCardHeader from '@/ui/MapCard/MapCardHeader';
import MapCardContent from '@/ui/MapCard/MapCardContent';
import MapCardActions from '@/ui/MapCard/MapCardActions';
import MapCardActionButton from '@/ui/MapCard/MapCardActionButton';
import DataController from '@/lib/DataController';
import MapCardHeaderButtonClose from '@/ui/MapCard/MapCardHeaderButtonClose';
import DialogContext from '@/context/DialogContext/DialogContext';
import UserContext from '@/context/UserContext/UserContext';
import modelComments from '@/models/comments';
import ImageSlider from '@/views/Comments/ImageSlider';
import Permissions from '@/lib/permissions';

// Types
import { MiscCardProps } from '@/@types/views/map';
import Image360 from '@/ui/Image360/Image360';

import { GFIFeatureType } from '@/@types/services/gsService';
import TenantCommentDialog from './TenantCommentDialog';

const MiscCard = (props: MiscCardProps) => {
  const { t } = useTranslation();

  const [loadStatus, setLoadStatus] = useState({ isLoading: true, isLoaded: false });
  const [comment, setComment] = useState<any>(null);

  // eslint-disable-next-line react/destructuring-assignment
  const [page360, setPage360] = useState<number>(props.featureData.type === 'photos360' ? 1 : -1);
  const [paginationDisabled, setPaginationDisabled] = useState(false);

  const { showDialog } = useContext(DialogContext);
  const userContext = useContext(UserContext);

  const dc = new DataController(modelComments);

  const { featureData, onClose, refreshLayers } = props;

  const { type } = featureData;

  const refreshComment = () => {
    // @ts-ignore
    setLoadStatus({ isLoading: true, isLoaded: false });
    dc.GetDataSingle(Number((featureData.features[0] as FeatureLike).getId()))
      .then((resp) => {
        if (resp.success && resp.data) {
          setComment(resp.data);
        } else {
          // setComments(null)
        }
      })
      .finally(() => {
        setLoadStatus({ isLoading: false, isLoaded: true });
      });
  };

  useEffect(() => {
    if (type === 'commentinfo') {
      refreshComment();
    }
  }, [featureData]);

  /* const handleCommentDialogClose = (result: any) => {
    if (result.dataChanged && result.action !== 'delete') {
      if (refreshPhotos && featureData.record && featureData.record.length !== 0) {
        refreshPhotos(featureData.features[0] as FeatureLike, undefined, featureData.position);
      } else {
        refreshComment();
      }
    } else if (result.action === 'delete') {
      if (onCommentDelete) {
        onCommentDelete();
      }
      onClose();
    } else if (refreshPhotos && featureData.record && featureData.record.length !== 0) {
      refreshPhotos(featureData.features[0] as FeatureLike, undefined, featureData.position);
    }
  }; */

  const handleEditClose = (result: any) => {
    if (result?.dataChanged) {
      refreshComment();
      if (refreshLayers) {
        refreshLayers();
      }
    }
  };

  /* const handleEditComment = () => {
    dialogContext.showDialog(CommentDialog, {
      dc,
      mode: 'update',
      form: 'insert',
      recordId: comment.id,
      onClose: handleCommentDialogClose,
      feature: featureData.features[0],
      refreshLayers: () => {
        if (refreshPhotos) {
          refreshPhotos(featureData.features[0] as FeatureLike, undefined, featureData.position);
        } else {
          refreshComment();
        }
      },
      // @ts-ignore
      initialRecord: {
        note: comment.note,
        photos: featureData.record && Array.isArray(featureData.record) && featureData.record?.length !== 0 ? featureData?.record : {},
      },
    });
  }; */

  const handleEditOpen = () => {
    showDialog(
      TenantCommentDialog,
      {
        mode: 'update',
        form: 'update',
        recordId: comment.id,
        onClose: handleEditClose,
      },
      1
    );
  };

  const showEdit =
    type === 'commentinfo' && !!comment?.id && !!userContext?.hasAnyPermission([Permissions.TenantCommentAdministration]);

  const statusValue = comment?.status_ttoken ? t(comment.status_ttoken) : comment?.status_id;
  const assignedToValue = comment?.assigned_to_name || '-';

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage360(value);
    setPaginationDisabled(true);
  };
  const finishLoad = () => {
    setPaginationDisabled(false);
  };
  const page360Size = featureData.type === 'photos360' && Array.isArray(featureData.features) ? featureData.features.length : 0;

  // const form = 'default';

  const title = featureData.type === 'photos360' ? undefined : comment?.created_by;
  const subheader = featureData.type === 'photos360' ? (featureData.features[page360 - 1] as GFIFeatureType).properties.id : comment?.id;

  const statusIcon =
    comment?.status_picker_icon_color && comment?.status_picker_icon ? (
      <Icon sx={{ color: comment.status_picker_icon_color }}>{comment.status_picker_icon}</Icon>
    ) : null;

  return (
    <MapCard
      sx={{
        backgroundColor: '#ececec',
        width: type === 'photos360' ? '615px' : '300px',
      }}
    >
      <>
        <MapCardHeader
          title={title}
          subheader={`${t('common.id')}: ${subheader}`}
          subheaderTypographyProps={{ color: 'initial' }}
          avatar={
            featureData.type === 'photos360' ? (
              <Vrpano sx={{ color: '#FFF' }} fontSize="large" />
            ) : (
              <CommentIcon sx={{ color: '#FFF' }} fontSize="large" />
            )
          }
          action={<MapCardHeaderButtonClose onClick={onClose} />}
        />
        <MapCardContent>
          {type === 'commentinfo' ? (
            loadStatus.isLoaded ? (
              <Box
                display="flex"
                flexDirection="column"
                m={1}
                sx={{
                  borderRadius: '5px',
                  boxShadow: '0px 3px 7px -4px rgba(0,0,0,0.60)',
                  WebkitBoxShadow: '0px 3px 7px -4px rgba(0,0,0,0.60)',
                  MozBoxShadow: '0px 3px 7px -4px rgba(0,0,0,0.60)',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box width="100%">
                  {featureData.record && featureData.record !== null && Array.isArray(featureData.record) ? (
                    <ImageSlider images={featureData.record.map((rec) => rec.image)} />
                  ) : (
                    <ImageSlider images={[]} />
                  )}
                </Box>
                <Box width="100%" py={0.75} px={2} minHeight="75px">
                  {comment && comment.note !== null ? (
                    <Box sx={{ textAlign: 'left', wordWrap: 'break-word' }}>{comment.note}</Box> // todo fix , use form controller maybe?
                  ) : null}
                </Box>
              </Box>
            ) : (
              <CircularProgress />
            )
          ) : type === 'photos360' ? (
            <Stack spacing={1} alignContent="center">
              <Image360
                finishLoad={finishLoad}
                imageId={(featureData.features[page360 - 1] as GFIFeatureType).properties.id as number}
                northOffset={(featureData.features[page360 - 1] as GFIFeatureType).properties.orijentacija as number}
              />
              <Pagination
                disabled={paginationDisabled}
                sx={{ display: 'flex', justifyContent: 'center' }}
                color="primary"
                count={page360Size}
                page={page360}
                onChange={handleChange}
              />
            </Stack>
          ) : null}
        </MapCardContent>
        <MapCardActions
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginTop: '0px',
            marginBottom: '5px',
            overflow: 'hidden',
          }}
        >
          {showEdit ? (
            <>
              <MapCardActionButton startIcon={<Edit />} onClick={handleEditOpen}>
                {t('titles.edit_comment')}
              </MapCardActionButton>
              <Box
                sx={{
                  width: '100%',
                  px: 1.5,
                  pt: 0.75,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#f8fafc',
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75} gap={1}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {t('comments.status')}
                    </Typography>
                    <Chip
                      size="small"
                      icon={statusIcon || undefined}
                      label={statusValue ?? '-'}
                      sx={{
                        maxWidth: '180px',
                        '& .MuiChip-icon': {
                          color: comment?.status_picker_icon_color || 'inherit',
                          opacity: 1,
                        },
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {t('comments.assigned_to')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        textAlign: 'right',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '180px',
                      }}
                    >
                      {assignedToValue}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </>
          ) : null}
          {
            // <MapCardActionButton startIcon={<EditIcon />} onClick={handleEditComment}>
            //  {t("buttons.edit_comment")}
            // </MapCardActionButton>
          }
        </MapCardActions>
      </>
    </MapCard>
  );
};

export default MiscCard;
